import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useFilteredAppMenuGroups } from "hooks/useFilteredAppMenuGroups";

function normalizePath(pathname = "") {
  return pathname.replace(/^\/+/, "").replace(/\/+$/, "");
}

function pathMatches(pathname, routeLink) {
  const normalizedRoute = normalizePath(routeLink);
  if (!normalizedRoute) return false;
  return (
    pathname === normalizedRoute || pathname.startsWith(`${normalizedRoute}/`)
  );
}

const HOVER_CLOSE_MS = 220;
const SCROLL_EDGE_EPSILON = 2;

const HorizontalAppMenu = ({ embedded = false }) => {
  const location = useLocation();
  const menuGroups = useFilteredAppMenuGroups();
  const currentPath = normalizePath(location.pathname);
  const menuRef = useRef(null);
  const scrollInnerRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRefs = useRef({});
  const hoverCloseTimer = useRef(null);
  const [openKey, setOpenKey] = useState(null);
  const [panelRect, setPanelRect] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const clearHoverClose = useCallback(() => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    clearHoverClose();
    hoverCloseTimer.current = setTimeout(() => setOpenKey(null), HOVER_CLOSE_MS);
  }, [clearHoverClose]);

  const updatePanelPosition = useCallback(() => {
    if (!openKey) {
      setPanelRect(null);
      return;
    }
    const el = triggerRefs.current[openKey];
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelRect({
      top: r.bottom + 4,
      left: r.left,
      minWidth: Math.max(r.width, 220),
    });
  }, [openKey]);

  const updateScrollArrows = useCallback(() => {
    const el = scrollInnerRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_EPSILON);
    setCanScrollRight(
      scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_EPSILON
    );
  }, []);

  const scrollMenu = useCallback((direction) => {
    const el = scrollInnerRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.65, 220);
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }, []);

  useEffect(() => {
    setOpenKey(null);
  }, [location.pathname]);

  useLayoutEffect(() => {
    updatePanelPosition();
  }, [openKey, updatePanelPosition]);

  useEffect(() => {
    updateScrollArrows();
    const el = scrollInnerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateScrollArrows());
    observer.observe(el);
    return () => observer.disconnect();
  }, [menuGroups.length, updateScrollArrows]);

  useEffect(() => {
    const onResize = () => updateScrollArrows();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateScrollArrows]);

  useEffect(() => {
    if (!openKey) return;
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    const inner = scrollInnerRef.current;
    inner?.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      inner?.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [openKey, updatePanelPosition]);

  useEffect(() => {
    if (!openKey) return;
    const onPointerDown = (event) => {
      const t = event.target;
      if (menuRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpenKey(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openKey]);

  useEffect(() => {
    if (!openKey) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openKey]);

  const getGroupSelectedRoute = (group) => {
    for (const item of group.items || []) {
      if (!item.permission) continue;
      if (item.children?.length) {
        const selectedChild = item.children.find(
          (child) =>
            child.permission && pathMatches(currentPath, child.routeLink)
        );
        if (selectedChild) return selectedChild.routeLink;
      } else if (pathMatches(currentPath, item.routeLink)) {
        return item.routeLink;
      }
    }
    return "";
  };

  const getGroupTriggerLabel = (group) => {
    const route = getGroupSelectedRoute(group);
    if (!route) return group.title;
    for (const item of group.items || []) {
      if (!item.permission) continue;
      if (item.children?.length) {
        const ch = item.children.find((c) => c.routeLink === route);
        if (ch) return ch.name;
      } else if (item.routeLink === route) {
        return item.name;
      }
    }
    return group.title;
  };

  const toggleGroup = (key) => {
    setOpenKey((k) => (k === key ? null : key));
  };

  const openGroupPanel = (key) => {
    clearHoverClose();
    setOpenKey(key);
  };

  const renderPanelBody = (group) => (
    <>
      {group.items.map((item, index) => {
        if (!item.permission) return null;
        if (item.children?.length) {
          return (
            <div
              key={item.key || item.name || index}
              className="horizontal-app-menu-dropdown-section"
            >
              <div
                className="horizontal-app-menu-dropdown-section-title"
                role="presentation"
              >
                {item.name}
              </div>
              {item.children.map((child) =>
                child.permission ? (
                  <Link
                    key={child.routeLink}
                    to={`/${child.routeLink}`}
                    role="menuitem"
                    className={`horizontal-app-menu-dropdown-link${
                      pathMatches(currentPath, child.routeLink)
                        ? " horizontal-app-menu-dropdown-link--active"
                        : ""
                    }`}
                    onClick={() => setOpenKey(null)}
                  >
                    {child.name}
                  </Link>
                ) : null
              )}
            </div>
          );
        }
        return (
          <Link
            key={item.routeLink}
            to={`/${item.routeLink}`}
            role="menuitem"
            className={`horizontal-app-menu-dropdown-link${
              pathMatches(currentPath, item.routeLink)
                ? " horizontal-app-menu-dropdown-link--active"
                : ""
            }`}
            onClick={() => setOpenKey(null)}
          >
            {item.name}
          </Link>
        );
      })}
    </>
  );

  const openGroup = openKey ? menuGroups.find((g) => g.key === openKey) : null;

  const portalPanel =
    openGroup && panelRect
      ? createPortal(
          <div
            ref={panelRef}
            className="horizontal-app-menu-custom-panel horizontal-app-menu-custom-panel--portal"
            style={{
              position: "fixed",
              top: panelRect.top,
              left: panelRect.left,
              minWidth: panelRect.minWidth,
            }}
            id={`horizontal-menu-panel-${openGroup.key}`}
            role="menu"
            aria-labelledby={`horizontal-menu-trigger-${openGroup.key}`}
            onMouseEnter={clearHoverClose}
            onMouseLeave={scheduleHoverClose}
          >
            {renderPanelBody(openGroup)}
          </div>,
          document.body
        )
      : null;

  return (
    <nav
      ref={menuRef}
      className={`horizontal-app-menu text-white${
        embedded ? " horizontal-app-menu--embedded" : " bg-dark-blue"
      }`}
      aria-label="Application sections"
    >
      <div
        className={`horizontal-app-menu-scroll-wrap${
          canScrollLeft ? " can-scroll-left" : ""
        }${canScrollRight ? " can-scroll-right" : ""}`}
      >
        <button
          type="button"
          className={`horizontal-app-menu-scroll-btn horizontal-app-menu-scroll-btn--left${
            canScrollLeft ? " is-active" : ""
          }`}
          aria-label="Show previous menu items"
          title="Previous menu items"
          disabled={!canScrollLeft}
          onClick={() => scrollMenu(-1)}
        >
          <span className="horizontal-app-menu-scroll-btn-icon" aria-hidden>
            <i className="fa fa-angle-double-left" />
          </span>
          {/* <span className="horizontal-app-menu-scroll-btn-text">Prev</span> */}
        </button>
        <div
          ref={scrollInnerRef}
          className={`horizontal-app-menu-inner d-flex align-items-center${
            embedded ? "" : " px-2 py-2"
          }`}
          onScroll={() => {
            updateScrollArrows();
            if (openKey) updatePanelPosition();
          }}
        >
          {menuGroups.map((group) => (
            <div key={group.key} className="horizontal-app-menu-dropdown">
              <div
                ref={(el) => {
                  triggerRefs.current[group.key] = el;
                }}
                className={`horizontal-app-menu-select-wrap${
                  openKey === group.key ? " is-open" : ""
                }`}
                onMouseEnter={() => openGroupPanel(group.key)}
                onMouseLeave={scheduleHoverClose}
              >
                <button
                  type="button"
                  className="horizontal-app-menu-trigger-btn"
                  aria-expanded={openKey === group.key}
                  aria-haspopup="true"
                  aria-controls={`horizontal-menu-panel-${group.key}`}
                  id={`horizontal-menu-trigger-${group.key}`}
                  onClick={() => toggleGroup(group.key)}
                >
                  <span className="horizontal-app-menu-select-icon">
                    <i
                      className={`${group.icon || "fa fa-circle"} horizontal-app-menu-icon`}
                    />
                  </span>
                  <span className="horizontal-app-menu-trigger-label">
                    {getGroupTriggerLabel(group)}
                  </span>
                  <i className="bx bx-chevron-down horizontal-app-menu-trigger-chevron" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className={`horizontal-app-menu-scroll-btn horizontal-app-menu-scroll-btn--right${
            canScrollRight ? " is-active" : ""
          }`}
          aria-label="Show more menu items"
          title="More menu items"
          disabled={!canScrollRight}
          onClick={() => scrollMenu(1)}
        >
          {/* <span className="horizontal-app-menu-scroll-btn-text">Next</span> */}
          <span className="horizontal-app-menu-scroll-btn-icon" aria-hidden>
            <i className="fa fa-angle-double-right" />
          </span>
        </button>
      </div>
      {portalPanel}
    </nav>
  );
};

export default HorizontalAppMenu;
